import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableHeaderFilter } from "@lib/types/Api";
import { useEffect, useMemo, useState } from "react";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import ProviderCustomerMatrixModal from "@lib/components/PageComponents/Provider/ProviderCustomerMatrixModal";
import { ProviderCustomerMatrix } from "@lib/types/Provider";
import { useProviderCustomerMatrixQueries } from "@lib/apiQueries";
import { SharedContextProps } from "@lib/types";
import { AppsContainer } from "@lib/components/AppComponents";

const SharedPaymentIntegrations: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  providerId,
  customerId,
  paymentProvider,
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();

  const initialFilters = [
    ...(providerId ? [`providerId||$eq||${providerId}`] : []),
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["provider", "customer"],
    filter: initialFilters,
  });
  const {
    providerCustomerMatricesQuery,
    addProviderCustomerMatrixMutation,
    updateProviderCustomerMatrixMutation,
    deleteProviderCustomerMatrixMutation,
  } = useProviderCustomerMatrixQueries({
    params,
    messages,
    enabled: pageIsActive && (!!providerId || !!customerId),
  });

  const { data, isLoading, isFetching } = providerCustomerMatricesQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (deleteProviderCustomerMatrixMutation.isSuccess) {
      toggle(false);
    }
  }, [deleteProviderCustomerMatrixMutation.isSuccess]);

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<ProviderCustomerMatrix>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (providerMatrix: ProviderCustomerMatrix) => {
    setSelected(providerMatrix);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        apiData,
        params,
        messages,
        navigate,
        deleteQuery: deleteProviderCustomerMatrixMutation,
        updateQuery: updateProviderCustomerMatrixMutation,
        handleSelect,
      }),
    [apiData, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !!f.title) as TableHeaderFilter[];
  }, [params, state]);

  const title =
    customTitle || (messages["common.paymentIntegrations"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={providerCustomerMatricesQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          minusHeight={minusHeight}
        />
      </AppsContent>

      <ProviderCustomerMatrixModal
        openModal={openModal}
        toggleModal={toggle}
        mutationQuery={
          selected
            ? updateProviderCustomerMatrixMutation
            : addProviderCustomerMatrixMutation
        }
        providerMatrix={selected}
        defaultProvider={paymentProvider}
        defaultCustomer={selected?.customer}
      />
    </AppsContainer>
  );
};

export default SharedPaymentIntegrations;
